'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';

import { Container, CustomImage as Image } from '..';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import styles from './ProgressTabsSection.module.css';
import useIsCnOPlus from 'hooks/pricing/useIsCnOPlus';

interface ProgressTabsSectionProps {
  className?: string;
}

export const ProgressTabsSection = ({
  className,
}: ProgressTabsSectionProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setCompletedIndices] = useState(() => new Set<number>());
  const { getValue } = useIsCnOPlus();
  const [isMobile, setIsMobile] = useState(false);

  const gsapContext = useGsapContext();

  const ctaTexts = useMemo(
    () => [
      'Cycle Tracking',
      'Conception',
      getValue('Cycle Tracking Pro', 'Cycle Tracking Plus'),
      getValue('Conception Pro', 'Conception Plus'),
      'Pregnancy Insights',
    ],
    [getValue],
  );

  const imageSrcs = useMemo(
    () => [
      'web_v2/womens-health/carousel/lg/2.png/w_2560',
      'web_v2/womens-health/carousel/lg/3.png/w_2560',
      getValue(
        'web_v2/womens-health/carousel/lg/1.png/w_2560',
        'web_v2/womens-health/plus/commprehensive-view/desktop/cycle-tracking-plus-new.png/w_2560',
      ),
      getValue(
        'web_v2/womens-health/carousel/lg/4.png/w_2560',
        'web_v2/womens-health/plus/commprehensive-view/desktop/ovulation-plus.png/w_2560',
      ),
      'web_v2/womens-health/carousel/lg/5.png/w_2560',
    ],
    [getValue],
  );

  const imageSrcsMobile = useMemo(
    () => [
      // Add mobile image URLs here
      'web_v2/womens-health/carousel/sm/1.png/w_1080',
      'web_v2/womens-health/carousel/sm/2.png/w_1080',
      getValue(
        'web_v2/womens-health/carousel/sm/3.png/w_1080',
        'web_v2/womens-health/plus/commprehensive-view/mobile/cycle-tracking-plus-new.png/w_1080',
      ),
      getValue(
        'web_v2/womens-health/carousel/sm/4.png/w_1080',
        'web_v2/womens-health/plus/commprehensive-view/mobile/ovulation-plus.png/w_1080',
      ),
      'web_v2/womens-health/carousel/sm/5.png/w_1080',
    ],
    [getValue],
  );

  // Get appropriate image sources based on screen size
  const currentImageSrcs = isMobile ? imageSrcsMobile : imageSrcs;

  const sectionRef = useRef<HTMLElement>(null);
  const ctaRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const progressRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  // Handle responsive image switching
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!imageRefs.current) return;

    gsapContext.add(() => {
      // Set initial state - first image visible
      gsap.set(imageRefs.current, { autoAlpha: 0 });

      if (imageRefs.current[0]) {
        gsap.set(imageRefs.current[0], { autoAlpha: 1 });
      }

      const masterTimeline = gsap.timeline({
        repeat: -1,
        onRepeat: () => {
          setCompletedIndices(new Set<number>());
          setActiveIndex(0);
        },
      });

      // Create individual timelines for each CTA
      progressRefs.current.forEach((progress, index) => {
        if (!progress) return;

        const ctaTimeline = gsap.timeline({
          onStart: () => {
            setActiveIndex(index);
            // Reset all progress bars when starting the cycle over (first CTA)
            if (index === 0) {
              const progressElements = progressRefs.current;
              requestAnimationFrame(() => {
                progressElements.forEach((prog) => {
                  if (prog) {
                    gsap.set(prog, { width: '0%' });
                  }
                });
              });
            }
          },
          onComplete: () => {
            setCompletedIndices((prev) => new Set([...prev, index]));
          },
        });

        // Progress bar animation
        ctaTimeline.fromTo(
          progress,
          { width: '0%' },
          {
            width: '100%',
            duration: 5,
            ease: 'none',
          },
          0,
        );

        // Image transition logic
        if (index < currentImageSrcs.length - 1) {
          // Wait until progress is complete before transitioning images
          ctaTimeline.add(() => {
            // Transition to next image after progress completes
            gsap.to(imageRefs.current[index], {
              autoAlpha: 0,
              duration: 0.8,
              ease: 'linear',
              overwrite: 'auto',
              stagger: 0.1,
            });
            gsap.to(imageRefs.current[index + 1], {
              autoAlpha: 1,
              duration: 0.8,
              ease: 'linear',
              overwrite: 'auto',
              stagger: 0.1,
            });
          }, 5); // Start transition exactly when progress completes
        } else {
          // Last item - loop back to first
          ctaTimeline.add(() => {
            gsap.to(imageRefs.current[index], {
              autoAlpha: 0,
              duration: 0.8,
              ease: 'linear',
              overwrite: 'auto',
              stagger: 0.1,
            });
            gsap.to(imageRefs.current[0], {
              autoAlpha: 1,
              duration: 0.8,
              ease: 'linear',
              overwrite: 'auto',
              stagger: 0.1,
            });
          }, 5);
        }

        // Small delay between CTAs for smoother transitions
        if (index < ctaTexts.length - 1) {
          ctaTimeline.to({}, { duration: 0.1 }); // More performant than empty function
        }

        masterTimeline.add(ctaTimeline);
      });

      timelineRef.current = masterTimeline;
    }, sectionRef);

    return () => {
      // Cleanup on unmount
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
    };
  }, [gsapContext, currentImageSrcs]);

  const handleCTAClick = useCallback((index: number) => {
    if (!timelineRef.current) return;

    // Calculate the exact time position for the clicked index
    const timePosition = index * 5.1;

    // Update completed indices efficiently
    setCompletedIndices(() => {
      const newSet = new Set<number>();
      for (let i = 0; i < index; i++) {
        newSet.add(i);
      }
      return newSet;
    });

    // Batch DOM updates
    const progressElements = progressRefs.current;
    const imageElements = imageRefs.current;

    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      // Reset progress bars after the clicked one
      for (let i = index; i < progressElements.length; i++) {
        if (progressElements[i]) {
          gsap.set(progressElements[i], { width: '0%' });
        }
      }

      // Fill progress bars before the clicked one
      for (let i = 0; i < index; i++) {
        if (progressElements[i]) {
          gsap.set(progressElements[i], { width: '100%' });
        }
      }

      // Smoothly transition to the target image
      imageElements.forEach((img, imgIndex) => {
        if (img) {
          gsap.to(img, {
            autoAlpha: imgIndex === index ? 1 : 0,
            duration: 0.8,
            ease: 'linear',
            overwrite: 'auto',
          });
        }
      });

      // Seek to the exact position and play
      timelineRef.current?.seek(timePosition);
      timelineRef.current?.play();
      setActiveIndex(index);
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      className={
        className
          ? `${styles.sectionContainer} ${className}`
          : styles.sectionContainer
      }
    >
      <div className={styles.imagesContainer}>
        {currentImageSrcs.map((src, index) => (
          <div
            className={styles.imageWrapper}
            key={`${isMobile ? 'mobile' : 'desktop'}-${index}`}
            ref={(el) => {
              imageRefs.current[index] = el;
            }}
          >
            {src && (
              <Image
                src={src}
                alt={`Background ${index + 1}`}
                fill
                style={{ objectFit: 'cover', objectPosition: 'bottom' }}
                priority={index === 0} // Prioritize first image
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            )}
          </div>
        ))}
      </div>
      <Container>
        <div className={styles.contentParent}>
          <h2 className={styles.titleSection}>
            A comprehensive view of your cycle at every stage
          </h2>
          <div className={styles.ctaContainer}>
            {ctaTexts.map((text, index) => (
              <button
                className={styles.progressCTA}
                key={index}
                ref={(el) => {
                  ctaRefs.current[index] = el;
                }}
                data-active={activeIndex === index ? 'true' : 'false'}
                onClick={() => handleCTAClick(index)}
              >
                {text}
                <div
                  ref={(el) => {
                    progressRefs.current[index] = el;
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '0%',
                    background: 'rgba(0, 0, 0, 0.1)', // 10% alpha black loader
                    zIndex: 1,
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};
